<template>
  <v-dialog
    v-model="dialogModel"
    persistent
    scrollable
    max-width="720"
    transition="dialog-bottom-transition"
  >
    <v-card>
      <v-card-title class="headline grey lighten-2 text-xs-center" primary-title>
        Averages calc.
      </v-card-title>
      <v-card-text>
        <div class="grey lighten-4 pa-3 mb-3">
          <div
            class="layout align-center justify-space-between period-averages-toggle"
            @click="periodAveragesExpanded = !periodAveragesExpanded"
          >
            <div class="body-2 font-weight-medium pr-2">Period averages</div>
            <div class="layout align-center">
              <span v-if="!periodAveragesExpanded" class="caption grey--text text--darken-3 mr-3">
                <span class="mr-1">{{ formatDateLabel(dateFrom) }}</span>
                <span>To {{ formatDateLabel(dateTo) }}</span>
              </span>
              <v-icon small>{{ periodAveragesExpanded ? 'far fa-chevron-up' : 'far fa-chevron-down' }}</v-icon>
            </div>
          </div>
          <div v-show="periodAveragesExpanded" class="mt-2">
            <div class="caption grey--text text--darken-3 mb-2">
              Agent purchased_credit rows that point at an agent transaction log.
              Defaults to the last 30 days.
            </div>
            <v-layout row wrap>
              <v-flex xs12 sm6 pr-2>
                <st-date-field
                  v-model="dateFrom"
                  label="Averages from"
                  :max="dateTo"
                />
              </v-flex>
              <v-flex xs12 sm6>
                <st-date-field
                  v-model="dateTo"
                  label="Averages to"
                  :min="dateFrom"
                  :max="todayDate"
                />
              </v-flex>
            </v-layout>
            <v-btn
              small
              outline
              color="primary"
              class="mt-1 mb-2"
              :loading="statsLoading"
              :disabled="!dateFrom || !dateTo"
              @click="loadStats"
            >
              Update averages
            </v-btn>
            <v-progress-linear
              v-if="statsLoading"
              indeterminate
              color="primary"
              class="mb-2"
            />
            <v-alert v-if="statsError" type="error" outline class="mb-2">
              {{ statsError }}
            </v-alert>
            <v-layout v-if="stats" row wrap>
              <v-flex xs6 sm3 class="mb-2">
                <div class="caption grey--text text--darken-3">Mean top-up</div>
                <div class="subheading">{{ formatMoney(stats.meanAmountCents) }}</div>
              </v-flex>
              <v-flex xs6 sm3 class="mb-2">
                <div class="caption grey--text text--darken-3">Median top-up</div>
                <div class="subheading">{{ formatMoney(stats.medianAmountCents) }}</div>
              </v-flex>
              <v-flex xs6 sm3 class="mb-2">
                <div class="caption grey--text text--darken-3">Top-ups in period</div>
                <div class="subheading">{{ formatCount(stats.topupCount) }}</div>
              </v-flex>
              <v-flex xs6 sm3 class="mb-2">
                <div class="caption grey--text text--darken-3">Top-ups / day</div>
                <div class="subheading">{{ formatCount(stats.networkDailyAverage) }}</div>
              </v-flex>
              <v-flex xs6 sm3 class="mb-2">
                <div class="caption grey--text text--darken-3">Weekday top-ups / day</div>
                <div class="subheading">{{ formatCount(stats.weekdayNetworkDailyAverage) }}</div>
              </v-flex>
              <v-flex xs6 sm3 class="mb-2">
                <div class="caption grey--text text--darken-3">Top-ups / agent / weekday</div>
                <div class="subheading">{{ formatCount(stats.perAgentWeekdayAverage) }}</div>
              </v-flex>
            </v-layout>
          </div>
        </div>

        <div class="elevation-1 pa-3 mb-3 profit-calc-inputs">
          <div class="body-2 font-weight-medium mb-2">Calculator inputs</div>
          <v-layout row wrap mb-3>
            <v-flex xs12 sm6 pr-2>
              <v-text-field
                v-model="conversionRatePercent"
                type="number"
                label="Exchange rate"
                suffix="%"
                hint="Must be above 100% for the agent to keep a spread"
                persistent-hint
              />
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="targetProfitGhs"
                type="number"
                label="Profit target"
                :prefix="currencyPrefix"
                hint="Amount the agent wants to make in the month"
                persistent-hint
              />
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs12 sm6 pr-2>
              <v-text-field
                v-model="workingDays"
                type="number"
                label="Working days in the month"
              />
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="topupAmountGhs"
                type="number"
                label="Average top-up"
                :prefix="currencyPrefix"
                hint="Filled from the period mean; you can override it"
                persistent-hint
              />
            </v-flex>
          </v-layout>
          <v-alert v-if="rateHasNoProfit" type="warning" outline class="mt-2 mb-0">
            This rate has no agent profit. Ghana’s country default is 100%: the spread only appears above 100%.
          </v-alert>
        </div>

        <div class="light-blue lighten-5 pa-3">
          <div class="body-2 font-weight-medium mb-2">
            <span v-if="calc">Result: top-ups needed to hit {{ formatMoney(targetCents) }}</span>
            <span v-else>Result</span>
          </div>
          <v-layout v-if="calc" row wrap>
            <v-flex xs6 sm3 class="mb-2">
              <div class="caption grey--text text--darken-3">Per day</div>
              <div class="headline primary--text">{{ formatCount(calc.dailyExact) }}</div>
            </v-flex>
            <v-flex xs6 sm3 class="mb-2">
              <div class="caption grey--text text--darken-3">Top-ups in the month</div>
              <div class="subheading">{{ formatCount(calc.monthlyTopUps) }}</div>
            </v-flex>
            <v-flex xs6 sm3 class="mb-2">
              <div class="caption grey--text text--darken-3">Profit per top-up</div>
              <div class="subheading">{{ formatMoney(calc.profitPerCents) }}</div>
            </v-flex>
            <v-flex xs6 sm3 class="mb-2">
              <div class="caption grey--text text--darken-3">Float spent per top-up</div>
              <div class="subheading">{{ formatMoney(calc.agentSpendCents) }}</div>
            </v-flex>
            <v-flex xs6 sm6 class="mb-2">
              <div class="caption grey--text text--darken-3">Passenger credit issued in the month</div>
              <div class="subheading">{{ formatMoney(calc.monthCreditCents) }}</div>
            </v-flex>
            <v-flex xs6 sm6 class="mb-2">
              <div class="caption grey--text text--darken-3">Agent float consumed in the month</div>
              <div class="subheading">{{ formatMoney(calc.monthFloatCents) }}</div>
            </v-flex>
          </v-layout>
          <div v-else class="caption grey--text text--darken-3">
            Fill the calculator inputs above (rate above 100%, profit target, working days, and average top-up) to see the result.
          </div>
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn color="warning" flat @click="closeDialog">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import src from './src'
export default src
</script>

<style scoped>
.profit-calc-inputs /deep/ .v-messages__message {
  font-style: italic;
  opacity: 0.8;
}

.period-averages-toggle {
  cursor: pointer;
}
</style>
